import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Pagination,
  Spinner,
  Table,
} from "react-bootstrap";
import { Link } from "react-router";
import { Download, ExternalLink, RefreshCw } from "react-feather";
import { useGetLazy, usePost } from "../../api";
import { EmptyState, FilterBar } from "../../components/ui";
import {
  IOpenDataReadinessFilter,
  IOpenDataReadinessSnapshot,
  IOpenDataReadinessSnapshotsResponse,
} from "../../interfaces";
import { OverallPill, StatusPill } from "./readiness-common";

const PAGE_SIZE = 100;

const STATUS_OPTIONS = [
  { value: "", label: "Any" },
  { value: "green", label: "Ready" },
  { value: "amber", label: "Partial" },
  { value: "red", label: "Blocked" },
  { value: "error", label: "Errored" },
];

const TRISTATE_OPTIONS = [
  { value: "", label: "Any" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const emptyFilter: IOpenDataReadinessFilter = {
  date: "",
  search: "",
  countryCode: "",
  listId: "",
  checkoutTechnology: "",
  overallStatus: "",
  merchantMapStatus: "",
  productFeedStatus: "",
  promosStatus: "",
  hasMapCart: "",
  hasMapShipping: "",
  hasProductFeed: "",
  hasIngestedProducts: "",
  hasDealLink: "",
};

const Tile: React.FC<{
  label: string;
  value: number | string;
  hint: string;
  variant?: string;
}> = ({ label, value, hint, variant }) => (
  <Col xs={6} md={3} xl={2}>
    <Card className="h-100">
      <Card.Body className="py-2 px-3">
        <div className={`fs-4 fw-semibold ${variant ? `text-${variant}` : ""}`}>
          {typeof value === "number" ? value.toLocaleString() : value}
        </div>
        <div className="small fw-semibold">{label}</div>
        <div className="text-muted" style={{ fontSize: "0.72rem" }}>
          {hint}
        </div>
      </Card.Body>
    </Card>
  </Col>
);

const Row: React.FC<{
  snapshot: IOpenDataReadinessSnapshot;
  onRun: (qaItemId: string) => void;
  running: boolean;
}> = ({ snapshot: s, onRun, running }) => (
  <tr>
    <td>
      <div className="fw-semibold">{s.domain}</div>
      <div className="text-muted small">
        {s.brandName ?? s.listName ?? "—"}
      </div>
    </td>
    <td>
      <Badge bg="light" text="dark">
        {s.countryCode}
      </Badge>
    </td>
    <td
      className="small"
      title="Checkout type on the merchant record when this sweep ran"
    >
      {s.checkoutTechnology ?? <span className="text-muted">no merchant</span>}
    </td>
    <td>
      <StatusPill dimension={s.merchantMap} label="Map" />
      <StatusPill dimension={s.productFeed} label="Feed" />
      <StatusPill dimension={s.promos} label="Promos" />
    </td>
    <td className="small">
      {s.metrics.hasMapProduct && <Badge bg="light" text="dark" className="me-1">product</Badge>}
      {s.metrics.hasMapAutoTemplate && <Badge bg="light" text="dark" className="me-1">auto</Badge>}
      {s.metrics.hasMapCart && <Badge bg="light" text="dark" className="me-1">cart</Badge>}
      {s.metrics.hasMapShipping && <Badge bg="light" text="dark" className="me-1">address</Badge>}
      {!s.metrics.hasMerchantMap && <span className="text-muted">no map</span>}
    </td>
    <td className="text-end small">
      {(s.metrics.productCount ?? 0).toLocaleString()}
      <div className="text-muted" style={{ fontSize: "0.72rem" }}>
        {s.metrics.advertiserCount ?? 0} advertiser
        {(s.metrics.advertiserCount ?? 0) === 1 ? "" : "s"}
      </div>
      {(s.metrics.otherRegionProductCount ?? 0) > 0 && (
        <div
          className="text-muted"
          style={{ fontSize: "0.72rem" }}
          title="Ingested on this domain but attributed to other geos"
        >
          +{s.metrics.otherRegionProductCount.toLocaleString()} other geos
        </div>
      )}
    </td>
    <td className="text-end small">
      {s.metrics.promoWithCodeCount}/{s.metrics.promoCount}
      <div className="text-muted" style={{ fontSize: "0.72rem" }}>
        {s.metrics.hasDealLink ? "deal link" : "no deal"}
      </div>
    </td>
    <td>
      <OverallPill status={s.overallStatus} />
    </td>
    <td className="small text-muted">
      {new Date(s.checkedAt).toLocaleString()}
    </td>
    <td className="text-end">
      <Button
        size="sm"
        variant="outline-secondary"
        className="me-1"
        disabled={running}
        onClick={() => onRun(s.qaItemId)}
        title="Re-check this merchant now, ignoring the cached result"
      >
        {running ? <Spinner size="sm" animation="border" /> : <RefreshCw size={13} />}
      </Button>
      <Link
        to={`/open-data/validation?view=domain&domain=${encodeURIComponent(s.domain)}&country=${encodeURIComponent(s.countryCode)}`}
        className="btn btn-sm btn-outline-secondary"
        title="Open the full validation for this domain and geo"
      >
        <ExternalLink size={13} />
      </Link>
    </td>
  </tr>
);

export const OpenDataReadinessList: React.FC = () => {
  const [draft, setDraft] = useState<IOpenDataReadinessFilter>(emptyFilter);
  const [applied, setApplied] = useState<IOpenDataReadinessFilter>(emptyFilter);
  const [page, setPage] = useState(1);
  const [runningItem, setRunningItem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { data, loading, error, execute } =
    useGetLazy<IOpenDataReadinessSnapshotsResponse>();
  const { execute: post } = usePost<unknown, unknown>();
  const { execute: fetchCsv } = useGetLazy<string>();
  const [downloading, setDownloading] = useState(false);

  // `execute` is rebuilt whenever the auth context renders, so it is held in a
  // ref — otherwise it would re-trigger the effect below on an unrelated render.
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const filterParams = (filter: IOpenDataReadinessFilter) => {
    const params = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => {
      if (v) params.set(k, String(v));
    });
    return params;
  };

  const load = useCallback(
    (filter: IOpenDataReadinessFilter, pageNumber: number) => {
      const params = filterParams(filter);
      params.set("page", String(pageNumber));
      params.set("pageSize", String(PAGE_SIZE));
      executeRef
        .current(`open-data-readiness/snapshots?${params}`)
        .catch(() => undefined);
    },
    []
  );

  useEffect(() => {
    load(applied, page);
  }, [applied, page, load]);

  const activeCount = useMemo(
    () => Object.values(applied).filter(Boolean).length,
    [applied]
  );

  const totals = data?.totals;
  const pageCount = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const runItem = async (qaItemId: string) => {
    setRunningItem(qaItemId);
    setNotice(null);
    try {
      await post(`open-data-readiness/items/${qaItemId}/run`, {});
      setNotice("Queued — the runner picks it up within a few seconds.");
    } catch (err: any) {
      setNotice(err?.data?.message ?? "Could not queue that item.");
    } finally {
      setRunningItem(null);
    }
  };

  // Exports what the filter matches, not the page on screen — the server
  // re-runs the same query without pagination.
  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const csv = await fetchCsv(
        `open-data-readiness/snapshots.csv?${filterParams(applied)}`,
        { responseType: "text" }
      );
      if (!csv) return;
      const url = URL.createObjectURL(
        new Blob([csv], { type: "text/csv;charset=utf-8" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `open-data-readiness-${data?.date ?? "latest"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setNotice("Could not download the CSV.");
    } finally {
      setDownloading(false);
    }
  };

  const set = (patch: Partial<IOpenDataReadinessFilter>) =>
    setDraft((d) => ({ ...d, ...patch }));

  return (
    <div>
      {totals && (
        <div className="row g-2 mb-3">
          <Tile
            label="Fully ready"
            value={totals.greenCount}
            hint="map, feed and promos all green"
            variant="success"
          />
          <Tile
            label="Partial"
            value={totals.amberCount}
            hint="missing cart, address or ingestion"
            variant="warning"
          />
          <Tile
            label="Blocked"
            value={totals.redCount}
            hint="no map, no feed or nothing live"
            variant="danger"
          />
          <Tile
            label="Errored"
            value={totals.errorCount}
            hint="an API could not be reached"
          />
          <Tile
            label="Tracked"
            value={totals.itemCount}
            hint="merchants in this snapshot"
          />
          <Tile
            label="Ingesting"
            value={totals.withIngestedProductsCount}
            hint={`of ${totals.withProductFeedCount} with a feed in their own geo`}
          />
        </div>
      )}

      <FilterBar
        activeCount={activeCount}
        onSearch={() => {
          setPage(1);
          setApplied(draft);
        }}
        onClear={() => {
          setDraft(emptyFilter);
          setApplied(emptyFilter);
          setPage(1);
        }}
        summary={
          data
            ? `${data.total.toLocaleString()} merchants${data.date ? ` on ${data.date}` : ""}`
            : undefined
        }
        trailing={
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={downloading || !data?.total}
            onClick={downloadCsv}
            title="Download every merchant matching these filters, not just this page"
          >
            {downloading ? (
              <Spinner size="sm" animation="border" />
            ) : (
              <>
                <Download size={14} className="me-1" /> CSV
              </>
            )}
          </Button>
        }
      >
        <Col md={3}>
          <Form.Label className="small fw-bold">Domain</Form.Label>
          <Form.Control
            placeholder="e.g. samsung"
            value={draft.search ?? ""}
            onChange={(e) => set({ search: e.target.value })}
          />
        </Col>
        <Col md={2}>
          <Form.Label className="small fw-bold">Snapshot date</Form.Label>
          <Form.Select
            value={draft.date ?? ""}
            onChange={(e) => set({ date: e.target.value })}
          >
            <option value="">Latest</option>
            {(data?.availableDates ?? []).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label className="small fw-bold">Country</Form.Label>
          <Form.Select
            value={draft.countryCode ?? ""}
            onChange={(e) => set({ countryCode: e.target.value })}
          >
            <option value="">Any</option>
            {(data?.countryCodes ?? []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label className="small fw-bold">QA list</Form.Label>
          <Form.Select
            value={draft.listId ?? ""}
            onChange={(e) => set({ listId: e.target.value })}
          >
            <option value="">Any</option>
            {(data?.lists ?? []).map((l) => (
              <option key={l._id} value={l._id}>
                {l.name}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label className="small fw-bold">Checkout</Form.Label>
          <Form.Select
            value={draft.checkoutTechnology ?? ""}
            onChange={(e) => set({ checkoutTechnology: e.target.value })}
            title="Checkout type from the merchant record"
          >
            <option value="">Any</option>
            {(data?.checkoutTechnologies ?? []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
            <option value="none">No merchant record</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label className="small fw-bold">Overall</Form.Label>
          <Form.Select
            value={draft.overallStatus ?? ""}
            onChange={(e) => set({ overallStatus: e.target.value })}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={2}>
          <Form.Label className="small fw-bold">Merchant map</Form.Label>
          <Form.Select
            value={draft.merchantMapStatus ?? ""}
            onChange={(e) => set({ merchantMapStatus: e.target.value })}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label className="small fw-bold">Product feed</Form.Label>
          <Form.Select
            value={draft.productFeedStatus ?? ""}
            onChange={(e) => set({ productFeedStatus: e.target.value })}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label className="small fw-bold">Promos</Form.Label>
          <Form.Select
            value={draft.promosStatus ?? ""}
            onChange={(e) => set({ promosStatus: e.target.value })}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label className="small fw-bold">Cart mapped</Form.Label>
          <Form.Select
            value={draft.hasMapCart ?? ""}
            onChange={(e) => set({ hasMapCart: e.target.value })}
          >
            {TRISTATE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label className="small fw-bold">Address mapped</Form.Label>
          <Form.Select
            value={draft.hasMapShipping ?? ""}
            onChange={(e) => set({ hasMapShipping: e.target.value })}
          >
            {TRISTATE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label className="small fw-bold">Ingesting</Form.Label>
          <Form.Select
            value={draft.hasIngestedProducts ?? ""}
            onChange={(e) => set({ hasIngestedProducts: e.target.value })}
          >
            {TRISTATE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Col>
      </FilterBar>

      {notice && (
        <Alert variant="info" dismissible onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && (
        <Alert variant="danger">Could not load the readiness snapshot.</Alert>
      )}

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : !data || data.results.length === 0 ? (
            <EmptyState
              title="Nothing swept yet"
              message="Toggle a QA list into the Open Data sweep, then run it from the toolbar above."
            />
          ) : (
            <Table hover responsive size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Merchant</th>
                  <th>Geo</th>
                  <th>Checkout</th>
                  <th>Checks</th>
                  <th>Map coverage</th>
                  <th className="text-end">Products (geo)</th>
                  <th className="text-end">Codes</th>
                  <th>Overall</th>
                  <th>Checked</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.results.map((s) => (
                  <Row
                    key={s._id}
                    snapshot={s}
                    onRun={runItem}
                    running={runningItem === s.qaItemId}
                  />
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {pageCount > 1 && (
        <Pagination className="mt-3">
          <Pagination.Prev
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          />
          <Pagination.Item disabled>
            {page} / {pageCount}
          </Pagination.Item>
          <Pagination.Next
            disabled={page >= pageCount}
            onClick={() => setPage((p) => p + 1)}
          />
        </Pagination>
      )}
    </div>
  );
};
